import * as z from 'zod';

export const CreateModifierRequest = z.object({
  modifier: z
    .object({
      _id: z
        .string()
        .describe('Item modifier ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the item modifier is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the item modifier. <br />\n\nIgnored when creating an item modifier.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the item modifier was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the item modifier was updated.')
        .optional()
        .nullable(),
      name: z.string().describe('Item modifier display name.').min(1).max(200),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      inStock: z
        .boolean()
        .describe('Whether the modifier is in stock.\nDefault: `true`.')
        .optional()
        .nullable(),
      businessLocationIds: z.array(z.string()).max(100).optional(),
    })
    .describe('Item modifier to create.'),
});
export const CreateModifierResponse = z.object({
  _id: z
    .string()
    .describe('Item modifier ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the item modifier is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the item modifier. <br />\n\nIgnored when creating an item modifier.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the item modifier was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the item modifier was updated.')
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Item modifier display name.')
    .min(1)
    .max(200)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  inStock: z
    .boolean()
    .describe('Whether the modifier is in stock.\nDefault: `true`.')
    .optional()
    .nullable(),
  businessLocationIds: z.array(z.string()).max(100).optional(),
});
export const GetModifierRequest = z.object({
  modifierId: z
    .string()
    .describe('ID of the item modifier to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetModifierResponse = z.object({
  _id: z
    .string()
    .describe('Item modifier ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the item modifier is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the item modifier. <br />\n\nIgnored when creating an item modifier.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the item modifier was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the item modifier was updated.')
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Item modifier display name.')
    .min(1)
    .max(200)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  inStock: z
    .boolean()
    .describe('Whether the modifier is in stock.\nDefault: `true`.')
    .optional()
    .nullable(),
  businessLocationIds: z.array(z.string()).max(100).optional(),
});
export const ListModifiersRequest = z.object({
  options: z
    .object({
      modifierIds: z.array(z.string()).max(500).optional(),
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Maximum number of items to load.')
            .min(0)
            .max(500)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .optional()
            .nullable(),
        })
        .describe('Metadata of the paginated results.')
        .optional(),
    })
    .optional(),
});
export const ListModifiersResponse = z.object({
  modifiers: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Item modifier ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the item modifier is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the item modifier. <br />\n\nIgnored when creating an item modifier.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the item modifier was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the item modifier was updated.')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Item modifier display name.')
          .min(1)
          .max(200)
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        inStock: z
          .boolean()
          .describe('Whether the modifier is in stock.\nDefault: `true`.')
          .optional()
          .nullable(),
        businessLocationIds: z.array(z.string()).max(100).optional(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor pointing to the next page and the previous page in the list of results.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata of the paginated results.')
    .optional(),
});
export const UpdateModifierRequest = z.object({
  _id: z
    .string()
    .describe('Item modifier ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  modifier: z
    .object({
      _id: z
        .string()
        .describe('Item modifier ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the item modifier is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the item modifier. <br />\n\nIgnored when creating an item modifier.'
        ),
      _createdDate: z
        .date()
        .describe('Date and time the item modifier was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the item modifier was updated.')
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Item modifier display name.')
        .min(1)
        .max(200)
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      inStock: z
        .boolean()
        .describe('Whether the modifier is in stock.\nDefault: `true`.')
        .optional()
        .nullable(),
      businessLocationIds: z.array(z.string()).max(100).optional(),
    })
    .describe('Item Modifier with updated properties.'),
});
export const UpdateModifierResponse = z.object({
  _id: z
    .string()
    .describe('Item modifier ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the item modifier is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the item modifier. <br />\n\nIgnored when creating an item modifier.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the item modifier was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the item modifier was updated.')
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Item modifier display name.')
    .min(1)
    .max(200)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  inStock: z
    .boolean()
    .describe('Whether the modifier is in stock.\nDefault: `true`.')
    .optional()
    .nullable(),
  businessLocationIds: z.array(z.string()).max(100).optional(),
});
export const BulkCreateModifiersRequest = z.object({
  modifiers: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Item modifier ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the item modifier is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the item modifier. <br />\n\nIgnored when creating an item modifier.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the item modifier was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the item modifier was updated.')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Item modifier display name.')
          .min(1)
          .max(200)
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        inStock: z
          .boolean()
          .describe('Whether the modifier is in stock.\nDefault: `true`.')
          .optional()
          .nullable(),
        businessLocationIds: z.array(z.string()).max(100).optional(),
      })
    )
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether the created item modifiers are included in the response. <br />\nDefault: `false`.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkCreateModifiersResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for creation of the item modifer.')
          .optional(),
        modifier: z
          .object({
            _id: z
              .string()
              .describe('Item modifier ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the item modifier is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the item modifier. <br />\n\nIgnored when creating an item modifier.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the item modifier was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the item modifier was updated.')
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Item modifier display name.')
              .min(1)
              .max(200)
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields.')
              .optional(),
            inStock: z
              .boolean()
              .describe('Whether the modifier is in stock.\nDefault: `true`.')
              .optional()
              .nullable(),
            businessLocationIds: z.array(z.string()).max(100).optional(),
          })
          .describe('Created item modifier.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for Bulk Create Modifiers API call.')
    .optional(),
});
export const BulkUpdateModifiersRequest = z.object({
  modifiers: z
    .array(
      z.object({
        modifier: z
          .object({
            _id: z
              .string()
              .describe('Item modifier ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the item modifier is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the item modifier. <br />\n\nIgnored when creating an item modifier.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the item modifier was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the item modifier was updated.')
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Item modifier display name.')
              .min(1)
              .max(200)
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields.')
              .optional(),
            inStock: z
              .boolean()
              .describe('Whether the modifier is in stock.\nDefault: `true`.')
              .optional()
              .nullable(),
            businessLocationIds: z.array(z.string()).max(100).optional(),
          })
          .describe('Item modifier to update.')
          .optional(),
        mask: z.array(z.string()).optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether the updated item modifiers are included in the response.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkUpdateModifiersResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for the update of the item modifer.')
          .optional(),
        modifier: z
          .object({
            _id: z
              .string()
              .describe('Item modifier ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the item modifier is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the item modifier. <br />\n\nIgnored when creating an item modifier.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the item modifier was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the item modifier was updated.')
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Item modifier display name.')
              .min(1)
              .max(200)
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields.')
              .optional(),
            inStock: z
              .boolean()
              .describe('Whether the modifier is in stock.\nDefault: `true`.')
              .optional()
              .nullable(),
            businessLocationIds: z.array(z.string()).max(100).optional(),
          })
          .describe('Updated item modifier.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for Bulk Update Modifiers API call.')
    .optional(),
});
export const DeleteModifierRequest = z.object({
  modifierId: z
    .string()
    .describe('ID of the item modifier to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteModifierResponse = z.object({});
export const QueryModifiersRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          name: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z.array(z.object({})).optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
});
export const QueryModifiersResponse = z.object({
  modifiers: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Item modifier ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the item modifier is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the item modifier. <br />\n\nIgnored when creating an item modifier.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the item modifier was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the item modifier was updated.')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Item modifier display name.')
          .min(1)
          .max(200)
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        inStock: z
          .boolean()
          .describe('Whether the modifier is in stock.\nDefault: `true`.')
          .optional()
          .nullable(),
        businessLocationIds: z.array(z.string()).max(100).optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor pointing to the next page and the previous page in the list of results.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata of the paginated results.')
    .optional(),
});
export const CountModifiersRequest = z.object({
  options: z
    .object({
      filter: z
        .record(z.string(), z.any())
        .describe('Filter for counting modifiers.')
        .optional()
        .nullable(),
    })
    .optional(),
});
export const CountModifiersResponse = z.object({
  count: z.number().int().describe('Counted modifiers.').optional(),
});
export const BulkDeleteModifiersRequest = z.object({
  ids: z.array(z.string()).max(200),
});
export const BulkDeleteModifiersResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for modifiers deletion.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the API call.')
    .optional(),
});
